import React, { useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import SavingsIcon from "@mui/icons-material/Savings";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import BookIcon from "@mui/icons-material/Book";
import RedeemIcon from "@mui/icons-material/Redeem";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getHoldingsRequest,
  getBullionRatesRequest,
  getCarouselRequest,
  getSipBalanceRequest,
} from "../../redux/Dashboard/dashboardSlice";
import CarouselSection from "../CarouselSection/CarouselSlider";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

const SavingsScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    data: holdings = [],
    bullionRates = [],
    carousel,
    sipBalance,
  } = useSelector((state) => state.dashboard || {});
  const { appConfig } = useSelector((state) => state.dashboard || {});
  const decimal = appConfig?.decimalPrecision ?? 4;
  const featureToggle = appConfig?.featureToggle ?? {};

  useEffect(() => {
    dispatch(
      getHoldingsRequest({ jeweller: `${process.env.REACT_APP_JEWELLER_ID}` }),
    );
    dispatch(
      getBullionRatesRequest({ id: `${process.env.REACT_APP_JEWELLER_ID}` }),
    );
    dispatch(getCarouselRequest());
    dispatch(getSipBalanceRequest());
  }, [dispatch]);

  const [topCarousel, setTopCarousel] = React.useState(null);
  const [midCarousel, setMidCarousel] = React.useState(null);
  const [bottomCarousel, setBottomCarousel] = React.useState(null);

  useEffect(() => {
    if (carousel?.data?.length > 0) {
      let top = {},
        mid = {},
        bottom = {};

      carousel.data.forEach((e) => {
        switch (e.name) {
          case "savings_top":
            top = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          case "savings_middle":
            mid = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          case "savings_bottom":
            bottom = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          default:
            break;
        }
      });

      setTopCarousel(top);
      setMidCarousel(mid);
      setBottomCarousel(bottom);
    } else {
      setTopCarousel(null);
      setMidCarousel(null);
      setBottomCarousel(null);
    }
  }, [carousel]);

  const totalBalances = React.useMemo(() => {
    let totalMetalBalance = 0;
    let totalMoneyBalance = 0;

    if (holdings && Array.isArray(holdings)) {
      holdings.forEach((holding) => {
        const metalName = holding?.metalName;
        const availableBalance =
          (Number(holding.availableBalance) || 0) +
          (Number(holding.metalsipbalance) || 0);
        const buyRate = Number(holding.buyRate) || 0;
        const moneyBalance = Number(holding.moneysipbalance) || 0;

        if (metalName === "Money") {
          totalMoneyBalance += moneyBalance;
        } else {
          totalMetalBalance += availableBalance * buyRate;
        }
      });
    }

    return {
      totalMetalBalance,
      totalMoneyBalance,
    };
  }, [holdings]);

  const totalMetalHoldingsOnly = React.useMemo(() => {
    let total = 0;

    if (holdings && Array.isArray(holdings)) {
      holdings.forEach((item) => {
        if (item?.metalName !== "Money") {
          const qty = Number(item.availableBalance || 0);
          const rate = Number(item.buyRate || 0);
          total += qty * rate;
        }
      });
    }

    return total;
  }, [holdings]);

  const sipTotals = React.useMemo(() => {
    if (!sipBalance?.data) return { totalSipValue: 0, list: [] };

    let totalSipValue = 0;

    const list = sipBalance.data.map((sip) => {
      const installments = sip.sipInstallments || [];
      const paidInstallments = installments.filter(
        (inst) => inst.status.toLowerCase() === "paid",
      );

      const totalWeight = sip.weightbalance;
      const totalMoney = sip.moneybalance;

      const bullionRateObj = bullionRates.find(
        (rate) =>
          rate.rateType === "Buy" &&
          rate?.bullion?.metalName === sip?.bullion?.metalName &&
          rate?.bullion?.karat === sip?.bullion?.karat,
      );

      const buyRate = bullionRateObj
        ? Number(bullionRateObj.ratePerGram || 0)
        : 0;

      const value =
        sip?.bullion?.metalName === "Money" ? totalMoney : totalWeight * buyRate;
      totalSipValue += value;

      return {
        ...sip,
        totalWeight,
        totalMoney,
        value,
        installmentsDone: paidInstallments.length,
      };
    });

    return { totalSipValue, list };
  }, [sipBalance]);

  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  const bottomActionSx = {
    flex: "1 1 0",
    maxWidth: 140,
    minWidth: 0,
    flexDirection: "column",
    textTransform: "none",
    color: themeColors.primary,
    px: 0.5,
    whiteSpace: "normal",
    textAlign: "center",
    "& .MuiButton-startIcon": {
      margin: 0,
      mb: 0.5,
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: themeColors.scaffoldGradientColors,
        p: 2,
        pb: 10,
        fontFamily: "Inter, sans-serif",
      }}
    >
      {topCarousel && (
        <CarouselSection
          slides={topCarousel.slides}
          height={topCarousel.height}
          showNavigator={topCarousel.showNavigator}
          duration={topCarousel.duration}
          enabled={topCarousel.enabled}
        />
      )}
      {/* ---------- VALUE OF HOLDINGS / CASH BALANCE SECTION ---------- */}
      <Grid container spacing={2} sx={{ mt: 3, px: { xs: 0, sm: 2, md: 4 }, mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card
            sx={{
              background: themeColors.mediumContainer,
              border: `1px solid ${themeColors.scaffold}`,
              borderRadius: "20px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
              color: themeColors.text,
              width: "100%",
              transition: "transform 0.3s",
              "&:hover": { transform: "translateY(-5px)" },
            }}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <Typography
                variant="subtitle1"
                sx={{ color: themeColors.text, fontSize: "16px" }}
              >
                Value of Holdings
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  mt: 1,
                  fontSize: { xs: "1.4rem", sm: "2.125rem" },
                  wordBreak: "break-word",
                }}
              >
                {symbol}{totalBalances.totalMetalBalance.toFixed(decimal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Card
            sx={{
              background: themeColors.mediumContainer,
              border: `1px solid ${themeColors.scaffold}`,
              borderRadius: "20px",
              boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
              color: themeColors.text,
              width: "100%",
              transition: "transform 0.3s",
              "&:hover": { transform: "translateY(-5px)" },
            }}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <Typography
                variant="subtitle1"
                sx={{ color: themeColors.text, fontSize: "16px" }}
              >
                Cash Balance
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  mt: 1,
                  fontSize: { xs: "1.4rem", sm: "2.125rem" },
                  wordBreak: "break-word",
                }}
              >
                {symbol}{totalBalances.totalMoneyBalance.toFixed(decimal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ---------- METAL HOLDINGS LIST ---------- */}
      <Card
        sx={{
          borderRadius: 3,
          mb: 3,
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
        }}
      >
        <CardContent>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            mb={1}
            sx={{ color: themeColors.text }}
          >
            Metal Holdings
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {holdings && holdings.length > 0 ? (
            holdings.map((item, index) => {
              const color =
                item?.metalName === "Gold"
                  ? "linear-gradient(135deg, #FFD700, #DAA520)"
                  : item?.metalName === "Silver"
                    ? "linear-gradient(135deg, #c0c0c0ff, #E0E0E0)"
                    : "#9E9E9E";

              const qty = Number(item.availableBalance || 0);
              const value =
                item?.metalName === "Money"
                  ? item.moneysipbalance
                  : qty * Number(item.buyRate || 0);

              return (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: themeColors.mediumContainer,
                    border: `1px solid ${themeColors.scaffold}`,
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1.2,
                    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                  }}
                >
                  <Box display="flex" alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        width: 70,
                        height: 50,
                        borderRadius: "10%",
                        background: color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 2,
                      }}
                    >
                      {item?.metalName === "Gold" && (
                        <Typography>{item?.metalName}</Typography>
                      )}
                      {item?.metalName === "Silver" && (
                        <Typography>{item?.metalName}</Typography>
                      )}
                      {item?.metalName === "Money" && (
                        <Typography>&#8377;</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
                        {[
                          item?.metalName,
                          item?.karat || item?.bullion?.karat,
                          item?.purity || item?.bullion?.purity,
                        ]
                          .map((part) =>
                            String(part || "")
                              .replace(/^-+|-+$/g, "")
                              .trim(),
                          )
                          .filter(Boolean)
                          .join("-")}
                      </Typography>
                      {item?.metalName !== "Money" && (
                        <Typography
                          variant="body2"
                          sx={{ color: themeColors.text }}
                        >
                          {qty.toFixed(decimal)} gms
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Typography
                    fontWeight={700}
                    sx={{ color: themeColors.text, wordBreak: "break-word" }}
                  >
                    {symbol}{value.toFixed(decimal)}
                  </Typography>
                </Box>
              );
            })
          ) : (
            <Typography color="text.secondary" align="center" py={2}>
              No holdings found.
            </Typography>
          )}

          <Divider sx={{ mt: 2, mb: 1 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1.5,
              background: themeColors.primary,
              borderRadius: 2,
              border: `1px solid ${themeColors.scaffold}`,
            }}
          >
            <Typography sx={{ color: themeColors.text }}>
              Total Value
            </Typography>
            <Typography fontWeight={700} sx={{ color: themeColors.text }}>
              {symbol}{totalMetalHoldingsOnly.toFixed(decimal)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {midCarousel && (
        <CarouselSection
          slides={midCarousel.slides}
          height={midCarousel.height}
          showNavigator={midCarousel.showNavigator}
          duration={midCarousel.duration}
          enabled={midCarousel.enabled}
        />
      )}

      {/* ---------- SIP HOLDINGS ---------- */}
      <Card
        sx={{
          borderRadius: 3,
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
        }}
      >
        <CardContent>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            mb={1}
            sx={{ color: themeColors.text }}
          >
            SIP Holdings
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {sipTotals.list.map((sip, index) => (
            <Box
              key={index}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 2,
                background: themeColors.mediumContainer,
                border: `1px solid ${themeColors.scaffold}`,
                color: themeColors.text,
                boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
              }}
            >
              <Typography fontWeight={700}>
                {sip.sipplan.name} - {sip?.bullion?.metalName}{" "}
                {sip?.bullion?.karat || ""}k {sip?.bullion?.purity || ""}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Installments: {sip.installmentsDone} paid/ {sip.noOfMonths}
              </Typography>

              {sip?.metalName !== "Money" && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Weight: {sip.totalWeight.toFixed(decimal)} gms
                </Typography>
              )}

              <Typography fontWeight={700} sx={{ mt: 1 }}>
                Value: {symbol} {sip.value.toFixed(decimal)}
              </Typography>
            </Box>
          ))}

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 1.5,
              background: themeColors.primary,
              borderRadius: 2,
              border: `1px solid ${themeColors.scaffold}`,
            }}
          >
            <Typography sx={{ color: themeColors.text }}>
              Total Value
            </Typography>
            <Typography fontWeight={700} sx={{ color: themeColors.text }}>
              {symbol} {sipTotals.totalSipValue.toFixed(decimal)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {bottomCarousel && (
        <CarouselSection
          slides={bottomCarousel.slides}
          height={bottomCarousel.height}
          showNavigator={bottomCarousel.showNavigator}
          duration={bottomCarousel.duration}
          enabled={bottomCarousel.enabled}
        />
      )}

      {/* ---------- FLOATING BOTTOM BAR ---------- */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: { xs: 0, md: "90px" },
          right: 0,
          bgcolor: "#fff",
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 1, sm: 3, md: 5 },
          py: 1.5,
          px: 2,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
        }}
      >
        <Button
          startIcon={<BookIcon />}
          onClick={() => {
            navigate("/PassbookScreen");
          }}
          sx={bottomActionSx}
        >
          <Typography variant="caption">Passbook</Typography>
        </Button>

        {featureToggle.sip && (
        <Button
          startIcon={<TrendingUpIcon />}
          onClick={() => {
            navigate("/CustomerSipScreen");
          }}
          sx={bottomActionSx}
        >
          <Typography variant="caption">SIP Plan</Typography>
        </Button>
        )}
        
        {featureToggle.sell && (
          <Button
            startIcon={<SwapHorizIcon />}
            onClick={() => {
              navigate("/Sell");
            }}
            sx={bottomActionSx}
          >
            <Typography variant="caption">Sell</Typography>
          </Button>
        )}
        {featureToggle.redeem && (
          <Button
            startIcon={<RedeemIcon />}
            onClick={() => {
              navigate("/RedeemHistory");
            }}
            sx={bottomActionSx}
          >
            <Typography variant="caption">Redeem</Typography>
          </Button>
        )}
        <Button
          startIcon={<CloudOffIcon />}
          onClick={() => {
              navigate("/OfflineTransactions");
            }}
          sx={bottomActionSx}
        >
          <Typography variant="caption">Offline</Typography>
        </Button>
        {featureToggle.gift && (
          <Button
            startIcon={<VolunteerActivismIcon />}
            onClick={() => {
              navigate("/Gifts");
            }}
            sx={bottomActionSx}
          >
            <Typography variant="caption">Gift Friend</Typography>
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default SavingsScreen;
